import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Button,
  Snackbar,
  Alert,
  Link,
  Chip,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useLocation, useNavigate } from "react-router-dom";
import FilterMotion from "./MotionForm/FilterMotion";

const upper = (v) => String(v || "").toUpperCase().trim();

const labelTipo = (tipo = "", origen = "") => {
  const t = upper(tipo);
  const o = upper(origen);
  if (o === "INVENTARIO") {
    if (t === "ING") return "Ingreso Inventario";
    if (t === "EGR") return "Egreso Inventario";
    return "Inventario";
  }
  if (o === "VENTA") return "Venta";
  if (o === "COMPRA") return "Compra";
  if (t === "ING") return "Ingreso";
  if (t === "EGR") return "Egreso";
  return "";
};

const labelOrigen = (origen = "") => {
  const o = upper(origen);
  if (o === "VENTA") return "Venta";
  if (o === "COMPRA") return "Compra";
  if (o === "INVENTARIO") return "Inventario";
  return o;
};

const labelDocumento = (d) => {
  const tipo = upper(d?.tipo_documento);
  const num = String(d?.numero_documento_asociado || "").trim();
  const estado = upper(d?.estado);
  const sri = upper(d?.sri_estado);
  const origen = upper(d?.origen);

  if (origen === "VENTA") {
    if (estado === "BORRADOR" || sri === "BORRADOR") {
      return num ? `PREFACTURA ${num}` : "PREFACTURA";
    }
    return num ? `FACT ${num}` : "FACTURA";
  }

  if (origen === "INVENTARIO") {
    if (num) return num;
    return "MOVIMIENTO MANUAL";
  }

  if (!num) return "-";
  if (tipo === "FACTURA") return `FACT ${num}`;
  if (tipo) return `${tipo} ${num}`;
  return num;
};

const statusChip = (d) => {
  const origen = upper(d?.origen);
  const estado = upper(d?.estado);
  const sri = upper(d?.sri_estado);

  if (estado === "ANULADA" || sri === "ANULADA") {
    return { label: "Anulada", color: "error", variant: "filled" };
  }

  if (origen === "VENTA" && (estado === "BORRADOR" || sri === "BORRADOR")) {
    return { label: "Prefactura", color: "warning", variant: "filled" };
  }

  if (origen === "COMPRA") {
    if (estado === "ANULADA") {
      return { label: "Anulada", color: "error", variant: "filled" };
    }
    return { label: "Registrada", color: "success", variant: "filled" };
  }

  if (origen === "VENTA") {
    if (sri === "AUTORIZADO" || estado === "AUTORIZADO") {
      return { label: "Autorizada", color: "success", variant: "filled" };
    }

    if (sri === "NO AUTORIZADO" || sri === "NO_AUTORIZADO") {
      return { label: "Emitida", color: "info", variant: "outlined" };
    }

    return { label: "Emitida", color: "info", variant: "outlined" };
  }

  if (origen === "INVENTARIO") {
    return { label: "Aplicado", color: "success", variant: "filled" };
  }

  return { label: "Registrado", color: "default", variant: "outlined" };
};

const isAutoInventoryRow = (d) => {
  return Boolean(d?.es_movimiento_automatico);
};

const buildRouteId = (value, fallbackPrefix) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (raw.includes("-")) return raw;
  return `${fallbackPrefix}-${raw}`;
};

const buildRowRouteId = (d) => {
  const origen = upper(d?.origen);
  if (origen === "VENTA") return buildRouteId(d?.id, "V");
  if (origen === "COMPRA") return buildRouteId(d?.id, "C");
  return buildRouteId(d?.id, "I");
};

const buildInventoryMovementRouteId = (value) => buildRouteId(value, "I");

const headSx = {
  bgcolor: "#1565C0",
  color: "#fff",
  fontSize: 14,
  fontWeight: 700,
  py: 1.3,
  whiteSpace: "nowrap",
};

const text13 = {
  fontSize: 13,
  lineHeight: 1.25,
};

const linkSx = {
  fontSize: 13,
  fontWeight: 700,
  display: "inline-flex",
  cursor: "pointer",
  color: "#1565C0",
};

const AdminMotion = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [docs, setDocs] = useState([]);
  const [search, setSearch] = useState("");
  const [origenFilter, setOrigenFilter] = useState("TODOS");

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "success",
    message: "",
  });

  const fetchDocs = async () => {
    try {
      const r = await fetch("/core/movimientos/listar/", { credentials: "include" });
      const d = await r.json().catch(() => ({}));
      setDocs(d.movimientos || []);
    } catch {
      setDocs([]);
      setSnackbar({ open: true, type: "error", message: "No se pudo cargar la lista" });
    }
  };

  useEffect(() => {
    fetchDocs();
  }, []);

  useEffect(() => {
    const raw = sessionStorage.getItem("inventory_updated_snackbar");
    if (!raw) return;
    sessionStorage.removeItem("inventory_updated_snackbar");
    try {
      const s = JSON.parse(raw);
      if (s?.message) {
        setSnackbar({
          open: true,
          type: s.type || "success",
          message: s.message,
        });
        fetchDocs();
      }
    } catch {}
  }, []);

  useEffect(() => {
    const st = location.state && typeof location.state === "object" ? location.state : null;
    if (!st) return;

    const s = st.snackbar || st.snack || st.toast;
    const shouldRefresh = Boolean(st.refresh);

    if (s && s.message) {
      setSnackbar({
        open: true,
        type: s.type || "success",
        message: s.message,
      });
    }

    if (shouldRefresh) {
      fetchDocs();
    }

    if (s || shouldRefresh) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const filtered = useMemo(() => {
    const t = search.toLowerCase().trim();

    return (docs || []).filter((d) => {
      if (isAutoInventoryRow(d)) {
        return false;
      }

      const origen = upper(d.origen);
      const tipo = upper(d.tipo);

      let okOrigen = true;

      switch (upper(origenFilter || "TODOS")) {
        case "COMPRA":
          okOrigen = origen === "COMPRA";
          break;
        case "VENTA":
          okOrigen = origen === "VENTA";
          break;
        case "INVENTARIO_ING":
          okOrigen = origen === "INVENTARIO" && tipo === "ING";
          break;
        case "INVENTARIO_EGR":
          okOrigen = origen === "INVENTARIO" && tipo === "EGR";
          break;
        default:
          okOrigen = true;
      }

      const okSearch =
        !t ||
        String(d.codigo || "").toLowerCase().includes(t) ||
        String(d.fecha || "").toLowerCase().includes(t) ||
        String(d.descripcion || "").toLowerCase().includes(t) ||
        String(d.responsable || "").toLowerCase().includes(t) ||
        String(d.tipo_documento || "").toLowerCase().includes(t) ||
        String(d.numero_documento_asociado || "").toLowerCase().includes(t) ||
        String(d.estado || "").toLowerCase().includes(t) ||
        String(d.sri_estado || "").toLowerCase().includes(t) ||
        String(d.movimiento_inventario_codigo || "").toLowerCase().includes(t) ||
        labelDocumento(d).toLowerCase().includes(t);

      const fecha = String(d.fecha || "");
      const okDateFrom = !dateFrom || (fecha && fecha >= dateFrom);
      const okDateTo = !dateTo || (fecha && fecha <= dateTo);

      return okOrigen && okSearch && okDateFrom && okDateTo;
    });
  }, [docs, search, origenFilter, dateFrom, dateTo]);

  useEffect(() => {
    setPage(0);
  }, [search, origenFilter, dateFrom, dateTo]);

  const handleLogout = async () => {
    await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    localStorage.clear();
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ textTransform: "none", backgroundColor: "#1565C0", borderRadius: 2 }}
              onClick={() => navigate("/admin-panel/inventario/movimientos/nuevo")}
            >
              Agregar Movimiento
            </Button>
          </Box>

          <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: "0 10px 30px rgba(15,23,42,0.06)" }}>
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 19, color: "#0f172a" }}>
                Movimientos
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.4 }}>
                Consulta ingresos, egresos, compras, ventas y prefacturas
              </Typography>
            </Box>

            <FilterMotion
              search={search}
              setSearch={setSearch}
              origenFilter={origenFilter}
              setOrigenFilter={setOrigenFilter}
              dateFrom={dateFrom}
              setDateFrom={setDateFrom}
              dateTo={dateTo}
              setDateTo={setDateTo}
            />

            <TableContainer
              sx={{
                borderRadius: 2,
                border: "1px solid #e2e8f0",
                overflowX: "auto",
                overflowY: "hidden",
                maxWidth: "100%",
                "&::-webkit-scrollbar": { height: 10 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#cbd5e1",
                  borderRadius: 10,
                },
              }}
            >
              <Table size="small" sx={{ minWidth: 980 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headSx, minWidth: 115 }}>Fecha</TableCell>
                    <TableCell sx={{ ...headSx, minWidth: 220 }}>Código</TableCell>
                    <TableCell sx={{ ...headSx, minWidth: 220 }}>Documento</TableCell>
                    <TableCell sx={{ ...headSx, minWidth: 320 }}>Descripción</TableCell>
                    <TableCell sx={{ ...headSx, minWidth: 170 }}>Responsable</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filtered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((d) => {
                      const chip = statusChip(d);
                      const rowRouteId = buildRowRouteId(d);
                      const inventoryMovementRouteId = buildInventoryMovementRouteId(d.movimiento_inventario_id);
                      const inventoryMovementCode = String(d.movimiento_inventario_codigo || "").trim();
                      const mainCode = String(d.codigo || "").trim();
                      const showInventoryLink =
                        upper(d?.origen) === "VENTA" &&
                        inventoryMovementRouteId &&
                        inventoryMovementRouteId !== rowRouteId &&
                        inventoryMovementCode;

                      return (
                        <TableRow
                          key={String(d.id)}
                          hover
                          sx={{
                            "& td": { py: 1.15, borderBottom: "1px solid #edf2f7" },
                            "&:hover": { bgcolor: "#fafcff" },
                          }}
                        >
                          <TableCell sx={{ minWidth: 115 }}>
                            <Typography sx={{ ...text13, fontWeight: 600, color: "#0f172a" }}>
                              {d.fecha}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ minWidth: 220 }}>
                            {mainCode ? (
                              rowRouteId ? (
                                <Link
                                  href={`/admin-panel/inventario/movimientos/${rowRouteId}`}
                                  target="_blank"
                                  underline="hover"
                                  sx={linkSx}
                                >
                                  {mainCode}
                                </Link>
                              ) : (
                                <Typography sx={{ ...linkSx }}>
                                  {mainCode}
                                </Typography>
                              )
                            ) : null}

                            {showInventoryLink ? (
                              <Link
                                href={`/admin-panel/inventario/movimientos/${inventoryMovementRouteId}`}
                                target="_blank"
                                underline="hover"
                                sx={{
                                  ...linkSx,
                                  display: "block",
                                  mt: mainCode ? 0.45 : 0,
                                }}
                              >
                                {inventoryMovementCode}
                              </Link>
                            ) : null}

                            {!mainCode && !showInventoryLink ? (
                              <Typography sx={{ ...text13, color: "#0f172a", fontWeight: 600 }}>
                                -
                              </Typography>
                            ) : null}

                            <Typography sx={{ fontSize: 11.5, color: "#64748b", mt: 0.35 }}>
                              {labelTipo(d.tipo, d.origen)} / {labelOrigen(d.origen)}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ minWidth: 220 }}>
                            <Stack spacing={0.7} alignItems="flex-start">
                              <Typography sx={{ ...text13, fontWeight: 600, color: "#0f172a" }}>
                                {labelDocumento(d)}
                              </Typography>
                              <Chip
                                label={chip.label}
                                color={chip.color}
                                variant={chip.variant}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  borderRadius: 2,
                                  height: 24,
                                  "& .MuiChip-label": { px: 1, fontSize: 11 },
                                }}
                              />
                            </Stack>
                          </TableCell>

                          <TableCell
                            sx={{
                              minWidth: 320,
                              maxWidth: 320,
                            }}
                            title={d.descripcion || ""}
                          >
                            <Typography
                              sx={{
                                ...text13,
                                color: "#0f172a",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {d.descripcion || "-"}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ minWidth: 170 }}>
                            <Typography sx={{ ...text13, color: "#0f172a", fontWeight: 600 }}>
                              {d.responsable || "-"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                  {!filtered.length && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                        <Stack spacing={0.75} alignItems="center">
                          <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                            No hay movimientos registrados
                          </Typography>
                          <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                            Ajusta los filtros o cambia el rango de fechas
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(e, n) => setPage(n)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Filas por página:"
            />
          </Paper>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.type}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminMotion;